import React, { forwardRef } from "react";
import { range, round } from "lodash";
import { cva, type VariantProps } from "class-variance-authority";
import { Row } from "@/shared/ui/Blocks";
import { cn } from "@/shared/utils/shadcn";
import { useFeatureFlag } from "@/shared/hooks/useFeatureFlag";
import { FeatureFlagKey } from "@/types/feature-flags";

const evaluationScoreRowVariants = cva(
  "w-3 h-3 rounded-sm relative overflow-hidden",
  {
    variants: {
      variant: {
        default: "",
        numeric:
          "w-8 h-8 rounded-full border flex items-center justify-center text-sm font-medium bg-white",
      },
      colorScheme: {
        default: "",
        blue: "bg-blue-500 border-blue-500 text-white",
        white: "bg-white",
        "blue-light": "bg-blue-100 border-blue-100 text-blue-500",
      },
      selected: {
        true: "",
        false: "bg-gray-200",
      },
      scoreLevel: {
        high: "bg-green-500",
        medium: "bg-orange-500",
        low: "bg-red-500",
      },
    },
    compoundVariants: [
      {
        variant: "numeric",
        colorScheme: "white",
        selected: true,
        className: "text-blue-700 ",
      },
      {
        variant: "numeric",
        colorScheme: "white",
        selected: false,
        className: "border-grey-300 text-white",
      },
      {
        variant: "numeric",
        colorScheme: ["blue"],
        selected: false,
        className: "border-none bg-white text-blue-500",
      },
      {
        variant: "numeric",
        colorScheme: ["blue-light"],
        selected: false,
        className: "border-grey-300 bg-white text-gray-700",
      },
    ],
    defaultVariants: {
      variant: "default",
      colorScheme: "default",
      selected: false,
    },
  }
);

interface EvaluationScoreRowProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "selected">,
    Omit<VariantProps<typeof evaluationScoreRowVariants>, "scoreLevel"> {
  score: number;
  maxScore?: number;
  selected?: boolean;
  hasEmptyScore?: boolean;
}

export const EvaluationScoreRow = forwardRef<
  HTMLDivElement,
  EvaluationScoreRowProps
>(
  (
    {
      score,
      maxScore: propMaxScore,
      variant = "default",
      colorScheme = "default",
      selected = false,
      hasEmptyScore = false,
      ...props
    },
    ref
  ) => {
    const isFourPointScoring = useFeatureFlag(
      FeatureFlagKey.FOUR_POINT_SCORING
    );
    const defaultMaxScore = isFourPointScoring ? 4 : 5;
    const maxScore = propMaxScore ?? defaultMaxScore;
    const getScoreLevel = (score: number, maxScore: number) => {
      if (
        variant === "numeric" ||
        colorScheme === "blue" ||
        colorScheme === "blue-light" ||
        colorScheme === "white"
      ) {
        return undefined;
      }
      const normalizedScore = (score / maxScore) * maxScore;
      if (normalizedScore >= 4) return "high";
      if (normalizedScore >= 3) return "medium";
      if (normalizedScore >= 1) return "low";
      return undefined;
    };

    const fullBoxes = Math.floor(score);
    const fractionalPart = score - fullBoxes;

    if (variant === "numeric") {
      let scoreRange = Array.from({ length: maxScore }, (_, i) => i + 1);
      if (hasEmptyScore) {
        scoreRange = [0, ...scoreRange];
      }

      return (
        <Row ref={ref} className="items-center gap-x-2" {...props}>
          {scoreRange.map((value) => {
            const isSelected = value === round(score);
            return (
              <div
                key={value}
                className={cn(
                  evaluationScoreRowVariants({
                    variant,
                    colorScheme,
                    selected: isSelected,
                  })
                )}
              >
                {value === 0 ? "-" : value}
              </div>
            );
          })}
        </Row>
      );
    }

    return (
      <Row ref={ref} className="items-center relative" {...props}>
        <Row className="space-x-1">
          {[...range(maxScore)].map((_, index) => (
            <div
              key={index}
              className="w-[12px] h-[12px] bg-grey-300 rounded-sm"
            />
          ))}
        </Row>
        <Row className="space-x-1 absolute">
          {[...Array(maxScore)].map((_, index) => (
            <div
              key={index}
              className={cn(
                evaluationScoreRowVariants({
                  variant,
                  colorScheme,
                  scoreLevel: getScoreLevel(score, maxScore),
                })
              )}
              style={{
                width:
                  index === fullBoxes && fractionalPart > 0
                    ? `${fractionalPart * 12}px`
                    : "12px",
                display: index < score ? "block" : "none",
              }}
            />
          ))}
        </Row>
      </Row>
    );
  }
);

EvaluationScoreRow.displayName = "EvaluationScoreRow";
